import axios from "axios";
import { handleAzure401 } from "../security/AzureAuthService";
import SessionStorageService from "../services/SessionStorageService";
import { TOKEN_TYPE } from "../entities/Constants";
import { getEnvVar } from "./RuntimeConfig";

const isAdminApiRequest = (url?: string): boolean => {
  if (!url) {
    return false;
  }
  return /\/data\/admin\//i.test(url);
};

const isAdminGetRequestWithoutToken = (url?: string): boolean => {
  if (!url) {
    return false;
  }

  return /\/on-form-service\/data\/admin\/get\//i.test(url);
};

const buildRequestUrl = (config: any): string => {
  const base = config?.baseURL || "";
  const url = config?.url || "";
  if (typeof url !== "string") {
    return base || "";
  }
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  if (!base) {
    return url;
  }
  return base.replace(/\/+$/, "") + "/" + url.replace(/^\/+/, "");
};

const normalizeAuthToken = (token?: string | null): string | null => {
  if (!token) {
    return null;
  }

  const tokenParts = String(token)
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  const selectedToken = tokenParts.find(part => /^Bearer\s+/i.test(part)) || tokenParts[0] || "";
  const normalizedToken = selectedToken.replace(/^Bearer\s+/i, "").trim();
  return normalizedToken || null;
};

const getTokenFromStorageJson = (
  storageKey: string,
  tokenKeys: string[]
): string | null => {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    for (const key of tokenKeys) {
      const value = parsed?.[key];
      if (typeof value === "string" && value.trim()) {
        return normalizeAuthToken(value);
      }
    }
  } catch {
    return null;
  }

  return null;
};

const getSessionAuthToken = (): string | null =>
  getTokenFromStorageJson("authTokens", ["access_token", "accessToken", "token"]) ||
  getTokenFromStorageJson("bps_authTokens", ["access_token"]) ||
  getTokenFromStorageJson("autoTokens", ["access_token", "accessToken"]) ||
  getTokenFromStorageJson("userProfile", ["token", "access_token", "accessToken"]);

const isAuthDebugEnabled = (): boolean => {
  try {
    const envFlag = (getEnvVar('AUTH_DEBUG') || "").toString().toLowerCase();
    if (envFlag === "1" || envFlag === "true") {
      return true;
    }

    const sessionFlag = (sessionStorage.getItem("auth-debug") || "").toLowerCase();
    return sessionFlag === "1" || sessionFlag === "true";
  } catch {
    return false;
  }
};

const resolveTokenSource = (authMethod?: string | null): string => {
  if (authMethod) {
    if (resolveTokenByAuthMethod(authMethod)) {
      return `authMethod:${normalizeAuthMethod(authMethod)}`;
    }
  }

  if (getProgramContainerProfileToken()) {
    return "programContainer.Profile.token";
  }

  if (getTokenFromStorageJson("authTokens", ["access_token", "accessToken", "token"])) {
    return "sessionStorage.authTokens";
  }

  if (getTokenFromStorageJson("bps_authTokens", ["access_token"])) {
    return "sessionStorage.bps_authTokens";
  }

  if (getTokenFromStorageJson("autoTokens", ["access_token", "accessToken"])) {
    return "sessionStorage.autoTokens";
  }

  if (getTokenFromStorageJson("userProfile", ["token", "access_token", "accessToken"])) {
    return "sessionStorage.userProfile";
  }

  if (resolveAccessToken()) {
    return "resolveAccessToken";
  }

  return "none";
};

const authDebugLog = (transport: "axios" | "fetch" | "xhr", url: string, token?: string | null) => {
  if (!isAuthDebugEnabled()) {
    return;
  }

  const authMethod = getSelectedAuthMethod();
  const tokenSource = resolveTokenSource(authMethod);
  console.debug("[AuthDebug]", {
    transport,
    url,
    authMethod: authMethod || null,
    tokenResolved: !!token,
    tokenSource,
  });
};

const getProgramContainerProfileToken = (): string | null => {
  try {
    const token = (window as any)?.programContainer?.Profile?.token;
    return typeof token === "string" && token.trim() ? normalizeAuthToken(token) : null;
  } catch {
    return null;
  }
};

const getAnyAvailableToken = (): string | null =>
  getProgramContainerProfileToken() || getSessionAuthToken() || resolveAccessToken();

const hasAuthContext = (): boolean => !!getSelectedAuthMethod() || !!getAnyAvailableToken();

const resolveAccessToken = (): string | null => {
  return (
    SessionStorageService.getBPSToken() ||
    SessionStorageService.getPSToken() ||
    SessionStorageService.getAccessToken(TOKEN_TYPE.API) ||
    getTokenFromStorageJson("autoTokens", ["access_token", "accessToken"]) ||
    getTokenFromStorageJson("userProfile", ["token", "access_token", "accessToken"])
  );
};

const normalizeAuthMethod = (value?: string | null): string => {
  return (value || "").trim().toLowerCase();
};

const getProgramContainerAuthMethod = (): string | null => {
  try {
    const authRequired = (window as any)?.programContainer?.AuthRequired;
    if (typeof authRequired === "string" && authRequired.trim()) {
      return authRequired;
    }
  } catch {
    return null;
  }

  return null;
};

const getSelectedAuthMethod = (): string | null => {
  const authFromProgramContainer = getProgramContainerAuthMethod();
  if (authFromProgramContainer) {
    return normalizeAuthMethod(authFromProgramContainer) || null;
  }

  try {
    return normalizeAuthMethod(sessionStorage.getItem("authRequired")) || null;
  } catch {
    return null;
  }
};

const resolveTokenByAuthMethod = (authMethod?: string | null): string | null => {
  switch (normalizeAuthMethod(authMethod)) {
    case "bps":
      return SessionStorageService.getBPSToken();
    case "ad":
      return SessionStorageService.getAccessToken(TOKEN_TYPE.API);
    case "tk":
      return null;
    case "ps":
      return SessionStorageService.getPSFormatToken() || SessionStorageService.getPSToken();
    default:
      return null;
  }
};

const resolveRequestAccessToken = (url?: string): string | null => {
  const selectedAuthMethod = getSelectedAuthMethod();
  if (!selectedAuthMethod) {
    return getAnyAvailableToken();
  }

  return (
    resolveTokenByAuthMethod(selectedAuthMethod) ||
    getAnyAvailableToken()
  );
};

const hasAuthorizationHeader = (headers: any): boolean => {
  if (!headers) {
    return false;
  }

  if (typeof headers.get === "function") {
    return !!headers.get("Authorization");
  }

  return !!(headers.Authorization || headers.authorization);
};

const getAuthorizationHeaderValue = (headers: any): string | null => {
  if (!headers) {
    return null;
  }

  if (typeof headers.get === "function") {
    const value = headers.get("Authorization") || headers.get("authorization");
    if (!value) {
      return null;
    }
    const normalized = normalizeAuthToken(value);
    return normalized ? `Bearer ${normalized}` : null;
  }

  const value = headers.Authorization || headers.authorization || null;
  if (!value) {
    return null;
  }
  const normalized = normalizeAuthToken(value);
  return normalized ? `Bearer ${normalized}` : null;
};

const isMsalIdentityUrl = (url?: string): boolean => {
  if (!url) return false;
  return /^https:\/\/(login\.microsoftonline\.com|login\.microsoft\.com|login\.windows\.net|sts\.windows\.net|login\.chinacloudapi\.cn|login\.microsoftonline\.us|graph\.microsoft\.com)/i.test(url);
};

const isExternalThirdPartyUrl = (url?: string): boolean => {
  if (!url) return false;
  return /^https?:\/\/(api\.ipify\.org)/i.test(url);
};

const isProtectedApiUrl = (url?: string): boolean => {
  return !!url && hasAuthContext();
};

const shouldForceSelectedAuthToken = (url?: string, headers?: any): boolean => {
  if (!isProtectedApiUrl(url)) {
    return false;
  }

  const expectedToken = resolveTokenByAuthMethod(getSelectedAuthMethod()) || getSessionAuthToken();
  if (!expectedToken) {
    return false;
  }

  const currentAuth = getAuthorizationHeaderValue(headers);
  const currentBearer = currentAuth?.match(/^Bearer\s+(.+)$/i)?.[1]?.trim();
  if (!currentBearer || currentBearer === expectedToken) {
    return false;
  }

  return true;
};

const shouldAttachFetchAuth = (url?: string): boolean => {
  if (!url) {
    return false;
  }

  if (isMsalIdentityUrl(url) || isExternalThirdPartyUrl(url)) {
    return false;
  }

  return !!resolveRequestAccessToken(url) || isProtectedApiUrl(url);
};

const patchFetchAuthorization = () => {
  if (typeof window === "undefined" || typeof window.fetch !== "function") {
    return;
  }

  const globalWindow = window as any;
  if (globalWindow.__authFetchPatched) {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const requestUrl = typeof input === "string"
      ? input
      : input instanceof URL
      ? input.toString()
      : (input as Request).url;

    if (!shouldAttachFetchAuth(requestUrl)) {
      return originalFetch(input, init);
    }

    const token = resolveRequestAccessToken(requestUrl);
    authDebugLog("fetch", requestUrl, token);
    if (!token) {
      return originalFetch(input, init);
    }

    const mergedHeaders = new Headers(
      init?.headers || (typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined)
    );

    mergedHeaders.set("Authorization", `Bearer ${token}`);

    return originalFetch(input, {
      ...(init || {}),
      headers: mergedHeaders,
    });
  };

  globalWindow.__authFetchPatched = true;
};

patchFetchAuthorization();

const shouldAttachXhrAuth = (url?: string): boolean => {
  if (!url) {
    return false;
  }

  if (isMsalIdentityUrl(url) || isExternalThirdPartyUrl(url)) {
    return false;
  }

  if (isAdminGetRequestWithoutToken(url)) {
    return false;
  }

  return /\/api\//i.test(url) && !!resolveRequestAccessToken(url);
};

const patchXhrAuthorization = () => {
  if (typeof window === "undefined" || typeof XMLHttpRequest === "undefined") {
    return;
  }

  const globalWindow = window as any;
  if (globalWindow.__authXhrPatched) {
    return;
  }

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

  XMLHttpRequest.prototype.setRequestHeader = function (name: string, value: string) {
    if (typeof name === "string" && name.toLowerCase() === "authorization") {
      (this as any).__hasAuthorizationHeader = true;
      const existing = (this as any).__authorizationHeaderValue as string | undefined;
      const existingToken = normalizeAuthToken(existing || null);
      const incomingToken = normalizeAuthToken(value || null);

      if (existingToken && incomingToken && existingToken === incomingToken) {
        return;
      }

      (this as any).__authorizationHeaderValue = value;
    }

    return originalSetRequestHeader.call(this, name as any, value as any);
  };

  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null
  ) {
    try {
      (this as any).__authRequestUrl = typeof url === "string" ? url : url?.toString();
    } catch {
      (this as any).__authRequestUrl = "";
    }

    return originalOpen.call(this, method, url as any, async as any, username as any, password as any);
  };

  XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
    const requestUrl = (this as any).__authRequestUrl as string | undefined;
    const hasAuthorizationHeader = !!(this as any).__hasAuthorizationHeader;

    if (shouldAttachXhrAuth(requestUrl) && !hasAuthorizationHeader) {
      const token = resolveRequestAccessToken(requestUrl);
      authDebugLog("xhr", requestUrl || "", token);
      if (token) {
        try {
          this.setRequestHeader("Authorization", `Bearer ${token}`);
        } catch {
          // ignore header set failures
        }
      }
    }

    return originalSend.call(this, body as any);
  };

  globalWindow.__authXhrPatched = true;
};

patchXhrAuthorization();

axios.interceptors.request.use(
  (config) => {
    const sessionId = sessionStorage.getItem("x-session-id");
    config.headers["x-session-id"] = sessionId || window?.crypto?.randomUUID() || '';

    const requestUrl = buildRequestUrl(config);
    if (isAdminGetRequestWithoutToken(requestUrl) || isMsalIdentityUrl(requestUrl) || isExternalThirdPartyUrl(requestUrl)) {
      if (config?.headers && typeof (config.headers as any).delete === "function") {
        (config.headers as any).delete("Authorization");
      } else if (config?.headers) {
        delete (config.headers as any).Authorization;
        delete (config.headers as any).authorization;
      }
      return config;
    }

    const accessToken = resolveRequestAccessToken(requestUrl);
    authDebugLog("axios", requestUrl, accessToken);
    if (accessToken) {
      config.headers = {
        ...(config.headers || {}),
        Authorization: `Bearer ${accessToken}`,
      };
    }

    if (!config.baseURL) {
      config.baseURL = sessionStorage.getItem("api-base-url") || "";
    }
    return config;
  },

  (error) => {
    return Promise.reject(error);
  }
);

// Save API response with id field to sessionStorage for confirmation pages
axios.interceptors.response.use(
  (response) => {
    if (response.data && typeof response.data === 'object' && response.data.id) {
      try {
        sessionStorage.setItem('submitApiResponse', JSON.stringify(response.data));
      } catch (e) {
        // Silent fail
      }
    }
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config as any;

    if (status === 401 && originalRequest && !originalRequest.__adRetry) {
      const url = buildRequestUrl(originalRequest);
      if (isAdminApiRequest(url) && !isAdminGetRequestWithoutToken(url)) {
        originalRequest.__adRetry = true;
        try {
          const newAccessToken = await handleAzure401();
          if (newAccessToken) {
            originalRequest.headers = {
              ...(originalRequest.headers || {}),
              Authorization: `Bearer ${newAccessToken}`,
            };
            return axios(originalRequest);
          }
        } catch (refreshError) {
          // Fall through to reject
        }
      }
    }

    return Promise.reject(error);
  }
);
